import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { statusService } from '../services/statusService';
import { userService } from '../services/userService';
import Loading from '../components/common/Loading';
import StatusUpdatesTable from '../components/common/StatusUpdatesTable';
import MultiSelectDropdown from '../components/common/MultiSelectDropdown';
import { useAuth } from '../context/AuthContext';

const TeamPage = () => {
  const { user, loading: authLoading } = useAuth();
  const isEmployeeOnly = user?.roles?.length === 1 && user.roles[0] === 'Employee';
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [taskFilterId, setTaskFilterId] = useState('');

  const loadTeamStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await statusService.getStatusUpdates({
        startDate,
        endDate
      });
      setStatusUpdates(response.data || []);
    } catch (error) {
      console.error('Failed to load team status:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await userService.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isEmployeeOnly) {
      loadTeamStatus();
    }
  }, [startDate, endDate, authLoading, isEmployeeOnly, loadTeamStatus]);

  useEffect(() => {
    if (!authLoading && !isEmployeeOnly) {
      loadUsers();
    }
  }, [authLoading, isEmployeeOnly, loadUsers]);

  const allTasksWithUpdates = useMemo(() => {
    const taskMap = new Map();
    statusUpdates.forEach(update => {
      if (update.task?.id && !taskMap.has(update.task.id)) {
        taskMap.set(update.task.id, update.task);
      }
    });
    return Array.from(taskMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [statusUpdates]);

  const visibleTasks = useMemo(() => allTasksWithUpdates, [allTasksWithUpdates]);

  const filteredUpdates = statusUpdates.filter(update => {
    const matchesPriority = !filterPriority || update.task?.priority === filterPriority;
    const matchesStatus = !filterStatus || update.status === filterStatus;
    const matchesUser = selectedUserIds.length === 0 || selectedUserIds.includes(String(update.user?.id));
    const matchesTask = !taskFilterId || String(update.task?.id) === String(taskFilterId);

    return matchesPriority && matchesStatus && matchesUser && matchesTask;
  });

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (endDate && value > endDate) {
      setEndDate(value);
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (startDate && value < startDate) {
      setStartDate(value);
    }
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    if (preset === 'today') {
      const value = today.toISOString().split('T')[0];
      setStartDate(value);
      setEndDate(value);
      return;
    }
    const days = preset === 'last7' ? 6 : 29;
    const start = new Date();
    start.setDate(today.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  if (authLoading) {
    return <Loading />;
  }

  if (isEmployeeOnly) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) return <Loading />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Team Status Updates</h1>
        <p className="text-gray-600 mb-4">View status updates from all employees in the company</p>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quick Presets</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => applyDatePreset('today')}
                className="px-3 py-2 text-sm border rounded hover:bg-gray-50"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('last7')}
                className="px-3 py-2 text-sm border rounded hover:bg-gray-50"
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('last30')}
                className="px-3 py-2 text-sm border rounded hover:bg-gray-50"
              >
                Last 30 days
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-1/2 border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="w-1/2 border rounded px-3 py-2"
            />
          </div>
          
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
            {/* <input
              type="text"
              value={taskSearchTerm}
              onChange={(e) => setTaskSearchTerm(e.target.value)}
              placeholder="Search tasks..."
              className="w-full border rounded px-3 py-2 mb-2"
            /> */}
            <select
              value={taskFilterId}
              onChange={(e) => setTaskFilterId(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">All Tasks</option>
              {visibleTasks.map(task => (
                <option key={task.id} value={task.id}>
                  {task.title} ({task.hierarchyLevel})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Users</label>
            <MultiSelectDropdown
              options={users.map(u => ({ value: String(u.id), label: u.fullName || u.email || `User ${u.id}` }))}
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              placeholder="All Users"
            />
          </div>

          

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">All Priorities</option>
              <option value="P1">P1 - Critical</option>
              <option value="P2">P2 - High</option>
              <option value="P3">P3 - Medium</option>
              <option value="P4">P4 - Low</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="Not Started">Not Started</option>
              <option value="Planning">Planning</option>
              <option value="In Progress">In Progress</option>
              <option value="In Review">In Review</option>
              <option value="Blocked">Blocked</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredUpdates.length} of {statusUpdates.length} status updates
      </div>

      <StatusUpdatesTable updates={filteredUpdates} showUserColumn />
    </div>
  );
};

export default TeamPage;
